/**
 * Vue Router for insights-admin.
 *
 * Routes mirror the plan's SPA inventory
 * (`docs/history/admin-implementation/insights-admin.md` §2):
 *   /login              — sign-in landing
 *   /                   — estate overview (the landing page)
 *   /clients            — list every registered client
 *   /clients/data-plane — fleet-wide photon health + traffic
 *   /clients/:id        — client detail + edit/delete
 *   /users              — cross-tenant user listing
 *   /users/:id          — user detail (insights reachable + activity), tabbed
 *   /insights           — cross-tenant insight listing
 *   /insights/new       — paste-a-source insight creation form
 *   /insights/:client_id/:id — read-only insight view (mounts <lucent-insight>)
 *   /logs/*             — the cursor-paginated log streams, tabbed
 *
 * Auth guard: every navigation checks `/auth/me`; unauthenticated
 * users are bounced to /login. The admin-role check happened
 * server-side at /auth/callback — by the time a session cookie
 * exists, the user is an admin.
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { loadCurrentAdmin } from "./auth.ts";

const routes: RouteRecordRaw[] = [
  {
    // The landing page. `/` used to redirect to `/clients`, which
    // answered "what tenants exist" — a question an operator rarely
    // opens the tool to ask. This answers "what needs me?" instead.
    path: "/",
    name: "overview",
    component: () => import("./views/Overview.vue"),
  },
  {
    path: "/login",
    name: "login",
    component: () => import("./views/Login.vue"),
  },
  {
    path: "/clients",
    name: "clients",
    component: () => import("./views/Clients.vue"),
  },
  {
    // Fleet-wide photon health, the counterpart to the Data plane *tab* on
    // /clients/:id. A standalone route rather than a child of /clients:
    // it is its own page with its own hero, and the Clients list links across.
    //
    // Registered before `/clients/:id` for readability only — vue-router
    // scores a static segment above a dynamic one regardless of declaration
    // order, so `data-plane` can never be mistaken for a client id.
    // `tests/clients-routes.test.ts` pins that, because it is the framework's
    // behaviour rather than ours.
    path: "/clients/data-plane",
    name: "clients-data-plane",
    component: () => import("./views/ClientsDataPlane.vue"),
  },
  {
    path: "/clients/:id",
    name: "client-detail",
    component: () => import("./views/ClientDetail.vue"),
    props: true,
  },
  {
    // Cross-tenant flat user list. The per-client panel inside
    // /clients/:id covers drill-down management; this view is for
    // searching across the whole estate.
    path: "/users",
    name: "users",
    component: () => import("./views/Users.vue"),
  },
  {
    // Per-user detail. Tabbed like /logs rather than /clients/:id: each
    // tab runs its own fetch (the activity feed carries a cursor) and
    // both are cross-link targets, which is the documented trigger for
    // real child routes over a button row. See `chrome.css` → Tabs.
    path: "/users/:id",
    component: () => import("./views/UserDetail.vue"),
    props: true,
    children: [
      {
        // `user-detail` names the empty-path child, NOT the parent, so
        // that other pages can link to "this user" without picking a
        // tab. Naming the parent instead resolves to the layout with an
        // empty `<RouterView>` — vue-router warns about exactly this,
        // and `tests/user-detail-routes.test.ts` pins it.
        path: "",
        name: "user-detail",
        redirect: { name: "user-detail-insights" },
      },
      {
        path: "insights",
        name: "user-detail-insights",
        component: () => import("./views/UserDetailInsights.vue"),
        props: true,
      },
      {
        path: "activity",
        name: "user-detail-activity",
        component: () => import("./views/UserDetailActivity.vue"),
        props: true,
      },
    ],
  },
  {
    path: "/insights",
    name: "insights",
    component: () => import("./views/Insights.vue"),
  },
  {
    // Operator "paste an insight" form. Registered before the
    // `/insights/:client_id/:id` pattern for readability; vue-router
    // scores static segments above dynamic ones regardless of order.
    path: "/insights/new",
    name: "insight-create",
    component: () => import("./views/InsightCreate.vue"),
  },
  {
    path: "/insights/:client_id/:id",
    name: "insight-view",
    component: () => import("./views/InsightView.vue"),
    props: true,
  },
  {
    // The log streams. They were sibling nav items answering
    // the same question — "what happened, and when" — differing only in
    // which table they read, so they're one section with tabs now. The
    // parent is a layout: hero + tab strip + <RouterView>.
    //
    // Deliberately real routes rather than the button-driven tabs on
    // /clients/:id. Each stream carries its own filters and cursor, and an
    // operator following a cross-link (embed mint → that viewer's queries)
    // has to be able to land on one directly and hit back afterwards.
    path: "/logs",
    component: () => import("./views/Logs.vue"),
    children: [
      { path: "", redirect: { name: "logs-queries" } },
      {
        path: "queries",
        name: "logs-queries",
        component: () => import("./views/AuditLog.vue"),
      },
      {
        path: "embed-tokens",
        name: "logs-embed-tokens",
        component: () => import("./views/EmbedTokens.vue"),
      },
      {
        path: "admin-actions",
        name: "logs-admin-actions",
        component: () => import("./views/AdminActions.vue"),
      },
      {
        // What tenant *users* did, as opposed to what operators did.
        // Reads the `user_activity` view, which unions the action log
        // with the records already carried on `insight_versions`,
        // `insight_group_shares` and `insights`.
        path: "user-actions",
        name: "logs-user-actions",
        component: () => import("./views/UserActions.vue"),
      },
    ],
  },
  // Pre-tabs URLs. Bookmarks and any link that escaped the estate still
  // work. vue-router carries the incoming query across a redirect, so the
  // audit log's `?viewer_id=` deep link survives without restating it here
  // — `tests/logs-routes.test.ts` pins that, because it is the framework's
  // behaviour rather than ours.
  { path: "/audit-log", redirect: { name: "logs-queries" } },
  { path: "/embed-tokens", redirect: { name: "logs-embed-tokens" } },
  { path: "/admin-actions", redirect: { name: "logs-admin-actions" } },
  {
    // On-demand insights → identity-provider user reconciliation. Distinct
    // from the per-client `Users` panel — this view is the global
    // diff dashboard the operator opens when they suspect drift.
    path: "/sync/users",
    name: "sync-users",
    component: () => import("./views/UserSync.vue"),
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to) => {
  const me = await loadCurrentAdmin();
  if (to.name === "login") {
    return me ? { path: "/" } : true;
  }
  if (!me) {
    return { name: "login", query: { return_to: to.fullPath } };
  }
  return true;
});
